import React from "react";
import styled from "styled-components";
import { Check } from "lucide-react";
import IconWithFallback from "Global/IconWithFallback";
import { ICON_TYPES } from "@shared/common/constants";
import { hexToRGB } from "@shared/utils/misc";
import { CREATE_ACCOUNT_SUCCESS_MESSAGE_ID } from "testIds.ts";

interface SuccessAccount {
  exchange: string;
  name?: string;
}

// "My ByBit - USDT" already says "ByBit USDT": the exchange shows only when
// the account's name doesn't.
const letters = (text: string) => text.toLowerCase().replace(/[^a-z0-9]/g, "");
const namesExchange = (name: string, exchange: string) =>
  letters(name).includes(letters(exchange));

interface NewLookSuccessMessageProps {
  title: string;
  accounts: SuccessAccount[];
  compact?: boolean;
}

const NewLookSuccessMessage = ({
  title,
  accounts,
  compact,
}: NewLookSuccessMessageProps) => (
  <Wrapper data-testid={CREATE_ACCOUNT_SUCCESS_MESSAGE_ID}>
    <CheckTile>
      <Check size={14} strokeWidth={2.25} aria-hidden />
    </CheckTile>
    <Body>
      <Title>{title}</Title>
      {compact ? (
        <Muted>{accounts.map((account) => account.exchange).join(" · ")}</Muted>
      ) : (
        <List>
          {accounts.map((account, index) => (
            <Row key={index}>
              <IconWithFallback
                iconType={ICON_TYPES.EXCHANGE}
                coinOrExchangeName={account.exchange}
                iconStyle={{ width: 16, height: 16 }}
                pxIconSize={16}
              />
              <Name>{account.name ?? account.exchange}</Name>
              <Muted>
                {account.name && !namesExchange(account.name, account.exchange)
                  ? account.exchange
                  : ""}
              </Muted>
            </Row>
          ))}
        </List>
      )}
    </Body>
  </Wrapper>
);

export default NewLookSuccessMessage;

const Wrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  max-width: min(440px, calc(100vw - 64px));
`;
const CheckTile = styled.span`
  flex: none;
  display: inline-grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  color: ${(p) => p.theme.colors.buttonText};
  background: ${(p) => p.theme.colors.green};
  box-shadow: 0 0 0 4px ${(p) => hexToRGB(p.theme.colors.green, 0.15)};
`;
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;
const Title = styled.div`
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 14px;
  line-height: 22px;
  font-weight: 520;
`;
// Icon, name and exchange in aligned columns; a long name shortens.
const List = styled.div`
  display: grid;
  grid-template-columns: max-content minmax(0, max-content) max-content;
  justify-content: start;
  align-items: center;
  column-gap: 8px;
  row-gap: 4px;
`;
const Row = styled.div`
  display: contents;
  font-size: 13px;
  line-height: 18px;
  white-space: nowrap;
`;
const Name = styled.span`
  color: ${(p) => p.theme.colors.primaryText};
  font-size: 13px;
  line-height: 18px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Muted = styled.span`
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 13px;
  line-height: 18px;
  white-space: nowrap;
`;
